import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { EmptyState, ErrorState, ExternalIcon, LoadingState, UploadIcon } from "@signet/ui";
import { useCreatorPhotos, useMe } from "@signet/web-kit";
import { IgsPage } from "../shared.tsx";
import { AccountDrawer, TakeModal } from "./account.tsx";
import { fetchStock, fetchStockCreators, STOCK, stockKeys, TYPE_LABEL, type AccountType, type Stock } from "./api.ts";
import { SetupLine } from "./cards.tsx";
import { CreatePhone, groupPhones, PhoneLevel, PhonePicker, PosterLevel, PosterSwitch, SystemLevel } from "./levels.tsx";
import { BiosModal, ResultsPanel } from "./results.tsx";
import { LinkModal } from "./link.tsx";
import { UploadModal } from "./upload.tsx";
import { AppealsView } from "./appeal.tsx";
import { GraveyardView } from "./graveyard.tsx";
import { AddAccount } from "./add.tsx";
import { isThreeReason } from "./status.tsx";
import { appealOpen } from "../../shared/appeal.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Accounts: the boss's account testing SOP (2026-10-07), and where IG
   Suicide opens (Jay, 2026-10-08).

   Phones come at three levels (levels.tsx): everyone, one poster, one
   phone, chosen with the poster switcher and the phone picker, all held in
   the address. Posters land on their own phones and admins on everyone's.
   Each phone keeps its account type's colour, aged amber and soft reg teal,
   the one thing that must never be mixed. Free to take and Results are
   their own views, so nothing is a long scroll away.
   ────────────────────────────────────────────────────────────────────────── */

type View = "phones" | "free" | "appeals" | "graveyard" | "results";
const VIEWS: View[] = ["phones", "free", "appeals", "graveyard", "results"];

function FreeList({ accounts, assign, onTake, onOpen }: { accounts: Stock[]; assign: boolean; onTake: (id: string) => void; onOpen: (id: string) => void }) {
  const counts = { soft_reg: accounts.filter((a) => a.accountType === "soft_reg").length, aged: accounts.filter((a) => a.accountType === "aged").length };
  const [type, setType] = useState<AccountType>(counts.soft_reg || !counts.aged ? "soft_reg" : "aged");
  const shown = accounts.filter((a) => a.accountType === type);
  return (
    <section className="igs-free-view">
      <div className="igs-free-head">
        <p>Each account already has its setup and bio. Take one onto a phone that holds the same type.</p>
        <div className="igs-type-switch" role="tablist" aria-label="Account type">
          {(["soft_reg", "aged"] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={type === t} className={`igs-type-tab ${t === "aged" ? "aged" : "soft"}${type === t ? " on" : ""}`} onClick={() => setType(t)}>
              {TYPE_LABEL[t]} <b>{counts[t]}</b>
            </button>
          ))}
        </div>
      </div>
      {shown.length ? (
        <ul className="igs-free-list">
          {shown.map((a) => (
            <li key={a.id}>
              <button type="button" className="igs-free-name" onClick={() => onOpen(a.id)}>
                <strong>@{a.username}</strong>
                <span>{a.supplier}</span>
              </button>
              <SetupLine a={a} />
              {/* To look the account over before taking it (Jay, 2026-10-12). */}
              <a className="secondary-button igs-ig-open" href={`https://www.instagram.com/${encodeURIComponent(a.username)}/`} target="_blank" rel="noreferrer noopener">
                <ExternalIcon size={14} /> Open in Instagram
              </a>
              <button type="button" className="button brass igs-take-button" onClick={() => onTake(a.id)}>
                {assign ? "Assign" : "Take"}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={`No ${TYPE_LABEL[type].toLowerCase()} accounts waiting`} body="New ones show here once a supplier's file is uploaded." />
      )}
    </section>
  );
}

export function AccountsPage() {
  const search = useSearch({ strict: false } as never) as { view?: string; who?: string; phone?: string; open?: string };
  const navigate = useNavigate();
  const { can } = useMe();
  const view: View = VIEWS.includes(search.view as View) ? (search.view as View) : "phones";
  // Everything about where you are sits in the address, so Back works and a link lands on the same phone.
  const go = (next: Partial<{ view: View; who: string | null; phone: string | null; open: string | null }>) =>
    navigate({
      to: ".",
      search: ((prev: Record<string, unknown>) => {
        const out: Record<string, unknown> = { ...prev, ...next };
        if (out.view === "phones") delete out.view;
        for (const k of ["who", "phone", "open"]) if (!out[k]) delete out[k];
        return out;
      }) as never,
      replace: next.open !== undefined && Object.keys(next).length === 1,
    });

  const [taking, setTaking] = useState<string | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  const [upload, setUpload] = useState(false);
  const [bios, setBios] = useState(false);
  const list = useQuery({ queryKey: stockKeys.list("all"), queryFn: fetchStock, refetchInterval: 60_000, enabled: can("igsuicide.stock:view") });
  const photos = useCreatorPhotos(STOCK);
  // Only who Creators says works here, not every name seen on a row (Jay, 2026-10-19).
  const roster = useQuery({ queryKey: [...stockKeys.all, "creators"], queryFn: fetchStockCreators, staleTime: 60_000, enabled: can("igsuicide.stock:view") });
  const data = list.data;
  const manages = data?.manages ?? false;
  const accounts = data?.accounts ?? [];
  const today = data?.today ?? "";
  // On a phone means it has a phone; imported accounts have no take date.
  const free = accounts.filter((a) => !a.phone);
  const groups = useMemo(() => groupPhones(accounts, today, data?.allPhones), [accounts, today, data?.allPhones]);
  const posters = data?.posters ?? [];

  // Posters land on their own phones, everyone else (admins) on All (Jay, 2026-10-08).
  const who = search.who ?? (data && posters.some((p) => p.id === data.me) ? data.me : "all");
  const phone = who !== "all" ? search.phone ?? null : null;
  const theirs = groups.filter((g) => g.poster === who);
  // Someone with phones who is not in the switcher still gets a tab while you are looking at them.
  const extra = who !== "all" && !posters.some((p) => p.id === who) ? { id: who, username: "", name: theirs[0]?.posterName ?? "Poster" } : null;
  const canAct = (a: Pick<Stock, "poster">) => manages || a.poster === data?.me;
  const find = (id: string | null | undefined) => accounts.find((a) => a.id === id) ?? null;
  const opened = find(search.open);

  if (!can("igsuicide.stock:view")) return <Navigate to={"/ig-suicide" as never} replace />;

  const views: { value: View; label: string; count?: number }[] = [
    { value: "phones", label: "Phones" },
    { value: "free", label: "Free to take", count: free.length },
    // 3 reason bans still waiting on Instagram (the boss, 2026-10-15).
    { value: "appeals", label: "3 reason bans", count: accounts.filter((a) => isThreeReason(a) && appealOpen(a.appealState)).length },
    // Bans that can't get back, off their phones (the boss, 2026-10-21).
    { value: "graveyard", label: "Graveyard", count: accounts.filter((a) => a.offPhone).length },
    { value: "results", label: "Best and worst setups" },
  ];

  return (
    <IgsPage
      actions={
        <>
          <button type="button" className="secondary-button" onClick={() => setBios(true)}>Bios</button>
          {manages ? (
            <button type="button" className="button brass" onClick={() => setUpload(true)}>
              <UploadIcon size={14} /> Upload accounts
            </button>
          ) : null}
        </>
      }
    >
      <div className="igs-acc">
        <nav className="igs-views" role="tablist" aria-label="Accounts views">
          {views.map((v) => (
            <button key={v.value} type="button" role="tab" aria-selected={view === v.value} className={view === v.value ? "on" : ""} onClick={() => go({ view: v.value, open: null })}>
              {v.label}
              {v.count !== undefined ? <b>{v.count}</b> : null}
            </button>
          ))}
        </nav>

        {list.isLoading ? (
          <LoadingState />
        ) : list.isError || !data ? (
          <ErrorState title="Couldn't load the accounts" onRetry={() => list.refetch()} />
        ) : view === "free" ? (
          <FreeList accounts={free} assign={manages} onTake={setTaking} onOpen={(id) => go({ open: id })} />
        ) : view === "appeals" ? (
          <AppealsView accounts={accounts} photos={photos} onOpen={(id) => go({ open: id })} />
        ) : view === "graveyard" ? (
          <GraveyardView accounts={accounts} photos={photos} onOpen={(id) => go({ open: id })} />
        ) : view === "results" ? (
          <ResultsPanel />
        ) : (
          <>
            <div className="igs-levels-bar">
              <PosterSwitch posters={posters} who={who} manages={manages} extra={extra} onPick={(next) => go({ who: next, phone: null })} />
              {who !== "all" ? <PhonePicker phones={theirs} phone={phone} onPick={(label) => go({ phone: label })} /> : null}
              {can("igsuicide.stock:edit") ? (
                <CreatePhone posters={posters} who={who} manages={manages} me={data.me} phones={groups} onMade={(id, label) => go({ who: id, phone: label })} />
              ) : null}
              {can("igsuicide.stock:edit") ? (
                <AddAccount posters={posters} manages={manages} me={data.me} myPhones={data.phones} creators={roster.data?.creators ?? []} rosterKnown={roster.data?.known ?? !roster.isError} photos={photos} />
              ) : null}
            </div>
            {who === "all" ? (
              <SystemLevel groups={groups} posters={posters} accounts={accounts} today={today} onPoster={(id) => go({ who: id, phone: null })} onPhone={(id, label) => go({ who: id, phone: label })} />
            ) : phone ? (
              <PhoneLevel phones={theirs} phone={phone} photos={photos} canAct={canAct} manages={manages} onPhone={(label) => go({ phone: label })} onOpen={(id) => go({ open: id })} onLink={setLinking} />
            ) : (
              <PosterLevel phones={theirs} today={today} photos={photos} canAct={canAct} manages={manages} onPhone={(label) => go({ phone: label })} onOpen={(id) => go({ open: id })} onLink={setLinking} />
            )}
          </>
        )}
      </div>

      <AccountDrawer account={opened} all={accounts} manages={manages} canAct={opened ? canAct(opened) : true} onClose={() => go({ open: null })} onOpen={(id) => go({ open: id })} />
      <LinkModal account={find(linking)} onClose={() => setLinking(null)} />
      <TakeModal account={find(taking)} phones={data?.phones ?? []} creators={roster.data?.creators ?? []} rosterKnown={roster.data?.known ?? !roster.isError} photos={photos} posters={posters} assign={manages} me={data?.me ?? null} onClose={() => setTaking(null)} />
      <UploadModal open={upload} onClose={() => setUpload(false)} />
      <BiosModal open={bios} onClose={() => setBios(false)} manages={manages} />
    </IgsPage>
  );
}
